/** Minimum gap between `onUrlChange` fires. See spec §5.3. */
const MIN_FIRE_INTERVAL_MS = 100;

export type HookHistoryOptions = {
  getCurrentUrl: () => string;
  onUrlChange: (url: string) => void;
};

/**
 * Patch history APIs + attach popstate/hashchange listeners so the
 * caller observes every distinct URL visit. Returns an `unhook`
 * function that restores the pristine history methods and removes
 * all listeners.
 *
 * De-duplication rules (spec §5.3):
 *   1. Consecutive identical URLs are collapsed.
 *   2. A 100 ms floor guards against SPA routers that fire
 *      pushState/replaceState multiple times in one tick for a single
 *      user navigation. Within that window, only the most recent URL
 *      is queued as a trailing fire so the final URL is never lost.
 *      Real user navigations are always spaced well above 100 ms so
 *      the floor is a pure anti-flood guard.
 */
export function hookHistory(opts: HookHistoryOptions): () => void {
  let lastFiredUrl: string | null = opts.getCurrentUrl();
  // Initialized to 0 so the first URL change is never rate-limited.
  let lastFireTs = 0;
  let trailingTimer: ReturnType<typeof setTimeout> | null = null;

  const fire = (url: string) => {
    if (url === lastFiredUrl) return;
    lastFiredUrl = url;
    lastFireTs = Date.now();
    opts.onUrlChange(url);
  };

  const onChange = () => {
    const url = opts.getCurrentUrl();
    if (url === lastFiredUrl) return;
    const since = Date.now() - lastFireTs;
    if (since < MIN_FIRE_INTERVAL_MS) {
      if (trailingTimer !== null) clearTimeout(trailingTimer);
      trailingTimer = setTimeout(() => {
        trailingTimer = null;
        fire(opts.getCurrentUrl());
      }, MIN_FIRE_INTERVAL_MS - since);
      return;
    }
    fire(url);
  };

  const origPush = history.pushState.bind(history);
  const origReplace = history.replaceState.bind(history);
  history.pushState = function patchedPush(...args) {
    const r = origPush(...args);
    onChange();
    return r;
  } as History['pushState'];
  history.replaceState = function patchedReplace(...args) {
    const r = origReplace(...args);
    onChange();
    return r;
  } as History['replaceState'];

  window.addEventListener('popstate', onChange);
  window.addEventListener('hashchange', onChange);

  return function unhook() {
    if (trailingTimer !== null) {
      clearTimeout(trailingTimer);
      trailingTimer = null;
    }
    history.pushState = origPush;
    history.replaceState = origReplace;
    window.removeEventListener('popstate', onChange);
    window.removeEventListener('hashchange', onChange);
  };
}
